// project imports
import {
  MarketPeriodsType,
  PlanRevisionStatus,
  EMSMemberAccountStatus,
  UserAccountStatus,
  MetricValueType,
  DeviceRole,
  ConsumptionDeviceType,
  ProductionDeviceType
} from 'utils/constants';
import { StringParams } from './index';
import {
  CharacteristicsRk,
  DeviceInstallationStatus,
  ReservedCapacityGuardMode
} from 'utils/characteristic';
import {
  ActivityLogEventCode,
  DailyDiagramValidationReason,
  DispatcherValidationError
} from 'models/dispatcher';
import { i18nCaReasonCodes } from 'models/caReasonCodes';
import { HxPlanningDeviceColumn } from 'views/modules/Dispatcher/HxDashboard/HxPlanning/HxPlanningContent/HxPlanningContentDevice/hxPlanningContentDeviceUtils';
import { HxPlanningContentDeviceHeaderAction } from 'views/modules/Dispatcher/HxDashboard/HxPlanning/HxPlanningContent/HxPlanningContentDevice/HxPlanningContentDeviceHeader/hxPlanningContentDeviceHeaderUtils';

import { GridDensity } from 'components/dataGrid/dataGridModels';
import { PanelRkColumn } from 'views/modules/Dispatcher/RkDashboard/PanelRk/panelRkModels';
import { DeliveryPointValidationArguments } from 'views/modules/Eai/DeliveryPoints/DeliveryPointDetail/DeliveryPointCharacteristics/DeliveryPointReservedCapacityCharacteristics/deliveryPointReservedCapacityGuardingValidationSchema';
import { DeviceReservedCapacityGuardingCharacteristicsValidationArguments } from 'views/modules/Eai/Devices/DeviceDetail/Characteristics/Common/DeviceReservedCapacityGuardingUtils';
import { ReservedCapacityGuardingCheckResultStatus } from 'views/modules/Dispatcher/RkDashboard/rkDashboardTypes';
import { AlarmsColumn } from 'views/modules/System/Alarms/alarmsModels';
import { ReservedCapacityGuardingActionTypeEnum } from 'features/dispatcher/dispatcherReservedCapacityGuardingTypes';
import {
  AggregationFn,
  BenefitCostType,
  BenefitEvaluation,
  EconomicBenefitReportFields
} from 'views/modules/Reporting/EconomicBenefitReports/economicBenefitUtils';
import { ProductionModelProfileType } from 'components/productionModelConfiguration/productionModelUtils';

// ==============================|| MODELS ||============================== //

export enum Translation {
  SK = 'sk',
  EN = 'en'
}

// translation state
export type i18nTranslation = {
  header: {
    localization: StringParams;
    profile: i18nHeaderProfile;
  };
  module: {
    crossModule: {
      productionModel: {
        listOfModels: {
          actions: {
            addModel: string;
            editModel: string;
            removeModel: string;
            removeAllModels: string;
            showModel: string;
          };
          columns: {
            startDate: string;
            endDate: string;
            profileType: string;
          };
          isDefault: string;
          errors: {
            validationErrorInModel: string;
            overlappingDateRange: string;
            rangeError: string;
          };
        };
        modelDetail: {
          actions: {
            startDate: string;
            endDate: string;
            profileType: string;
          };
          columns: {
            period: string;
            commonProductionMwh: string;
            workdaysProductionMwh: string;
            weekendProductionMwh: string;
          };
          isDefault: string;
        };
        profileType: {
          [ProductionModelProfileType.COMMON]: string;
          [ProductionModelProfileType.WORKDAYS_WEEKENDS]: string;
        };
      };
    };
    dispatcher: {
      onlineDispatching: i18nDispatcherOnlineDispatching;
      productionPlanning: i18nDispatcherProductionPlanning;
      hxDashboard: i18nDispatcherHxDashboard;
      rkDashboard: i18nDispatcherRkDashboard;
      periodText: string;
      currentTime: string;
      dataGrid: {
        columns: {
          title: string;
        };
        density: {
          title: string;
          [GridDensity.COMPACT]: string;
          [GridDensity.STANDARD]: string;
          [GridDensity.COMFORTABLE]: string;
        };
      };
      messages: i18nDispatcherMessages;
      operationCalendar: i18nDispatcherOperationCalendar;
      codebooks: i18nDispatcherCodebooks;
      weatherWidget: i18nDispatcherWeatherWidget;
    };
    eai: {
      subjects: i18nEaiSubjects;
      users: i18nEaiUsers;
      balanceGroups: i18nEaiBalanceGroups;
      deliveryPoints: i18nEaiDeliveryPoints;
      devices: i18nEaiDevices;
      deliveryContracts: i18nEaiDeliveryContracts;
      codebooks: i18nEaiCodebooks;
      messages: i18nEaiMessages;
    };
    reporting: i18nReporting;
    homePage: i18nHomePage;
    system: {
      activityEvents: i18nSystemActivityEvents;
      codebooks: i18nSystemCodebooks;
      activityLogs: i18nSystemActivityLogs;
      alarms: i18nSystemAlarms;
    };
  };
  navigation: i18nNavigation;
  pages: {
    crashFallback: i18nCrashFallback;
  };
  dialogs: {
    selectEmsMemberAccount: i18nSelectEmsMemberAccount;
    addCredentials: i18nAddCredentials;
    removeCredentials: i18nRemoveCredentials;
    emsMemberAccountStateDialog: i18nEmsMemberAccountState;
    userAccountStateDialog: i18nUserAccountState;
    resetPasswordDialog: i18nResetPasswordDialog;
    deleteUserDialog: i18nRemoveEntityOrRelationDialog;
    deleteSubjectDialog: i18nDeleteSubjectDialog;
    unassignUserDialog: i18nRemoveEntityOrRelationDialog;
    deleteUserRoleDialog: i18nRemoveEntityOrRelationDialog;
    setDeviceStatusDialog: i18nSetDeviceStatusDialog;
    createGDSDialog: i18nCreateGDSState;
    changeDPAssignmentDialog: i18nChangeDPAssignment;
    deleteDevice: i18nRemoveEntityOrRelationDialog;
    deleteDeviceGroup: i18nRemoveEntityOrRelationDialog;
    unassignDeliveryPoint: i18nRemoveEntityOrRelationDialog;
    invalidateGDS: i18nDefaultDialog;
    businessTimeDialog: i18nBusinessTimeDialog;
  };
  common: {
    noItems: string;
    unitsOfMeasure: i18nUnitsOfMeasure;
    marketPeriod: i18nMarketPeriod;
    wizard: i18nCommonWizard;
    table: i18nCommonTable;
    button: i18nCommonButton;
    status: i18nCommonStatus;
    messages: i18nCommonMessages;
    chart: i18nCommonChart;
    httpErrorStatusMessages: i18nHttpErrorStatusMessages;
    qualityFlags: i18nCommonQualityFlags;
  };
};

// tables
type i18nCommonTable = {
  filter: string;
  inputSearch: string;
  rowsPerPage: string;
  of: string;
  moreThan: string;
  clearFilter: string;
  validFor: string;
  actions: string;
  current: string;
  selectAll: string;
  detail: string;
  delete: string;
  edit: string;
  resultsNotFound: string;
  unassign: string;
  unknownType: string;
  loading: string;
};

// navigation
export type i18nNavigation = {
  dispatcher: i18nNavigationDispatcher;
  digiTwin: i18nNavigationDigiTwin;
  reporting: i18nNavigationReporting;
  eai: i18nNavigationEai;
  system: i18nNavigationSystem;
};

type i18nNavigationDispatcher = {
  group: string;
  onlineDispatching: string;
  onlineDispatchingDeliveryPoint: string;
  onlineDispatchingDevice: string;
  productionPlanning: string;
  consumptionPlanning: string;
  operatingCalendar: string;
  operationCalendar: string;
  hxDashboard: string;
  rkDashboard: string;
};

type i18nNavigationDigiTwin = {
  group: string;
};

type i18nNavigationReporting = {
  group: string;
  marketImbalance: string;
  balancingGroup: string;
  deviceProductionPlan: string;
  deliveryPointProductionPlan: string;
  economicBenefits: string;
};

type i18nNavigationEai = {
  group: string;
  users: string;
  userList: string;
  userDetail: string;
  userAssignment: string;
  userWizard: string;
  subjects: string;
  subjectList: string;
  subjectDetail: string;
  subjectWizard: string;
  balanceGroups: string;
  balanceGroupDetail: string;
  deliveryPoints: string;
  deliveryPointList: string;
  deliveryPointDetail: string;
  deliveryPointWizard: string;
  deliveryPointGdsList: string;
  deliveryPointGdsDetail: string;
  devices: string;
  deviceList: string;
  deviceDetail: string;
  deviceWizard: string;
  deviceCharacteristicGroupWizard: string;
  deviceGroupDetail: string;
  deliveryContracts: string;
  deliveryContractWizard: string;
  deliveryContractDetail: string;
};

type i18nNavigationSystem = {
  group: string;
  activityEvents: string;
  alarms: string;
};
type i18nHeaderProfile = {
  darkMode: string;
  logout: string;
  changeSubject: string;
  version: string;
};

// module - dispatcher
export type i18nDispatcherOnlineDispatching = {
  title: string;
  balanceGroupImbalance: i18nDispatcherOnlineDispatchingImbalance;
  systemValues: i18nDispatcherOnlineDispatchingSystemValues;
  productionPlan: i18nDispatcherOnlineDispatchingProductionPlan;
  deliveryPoint: i18nDispatcherOnlineDispatchingDeliveryPoint;
  device: i18nDispatcherOnlineDispatchingDevice;
  overviewTable: i18nDispatcherOnlineDispatchingOverviewTable;
};

export type i18nDispatcherOnlineDispatchingImbalance = {
  title: string;
  plannedImbalanceD1: string;
  plannedImbalanceHX: string;
  actualImbalance: string;
};

export type i18nDispatcherOnlineDispatchingSystemValues = {
  title: string;
  system: string;
  price: string;
  predictedPriceIDM15: string;
  predictedPriceIDM60: string;
  predictedSystemImbalance: string;
  systemImbalance: string;
  actualSystemImbalance: string;
};

export type i18nDispatcherOnlineDispatchingProductionPlan = {
  title: string;
  plannedProduction: string;
  predictedProduction: string;
  actualProduction: string;
};

export type i18nDispatcherOnlineDispatchingOverviewTable = {
  title: string;
  periodText: string;
  input: {
    Filter: string;
    DeliveryPoints: string;
    BalancingGroups: string;
  };
  columns: {
    deliveryPointName: string;
    deviceName: string;
    communicationStatus: string;
    actualProduction: string;
    plannedProduction: string;
    productionFulfillment: string;
    actions: string;
  };
};

export type i18nDispatcherOnlineDispatchingDeliveryPoint = {
  periodText: string;
  systemValuesState: i18nDispatcherDeliveryPointSystemValuesState;
  operationEvents: i18nDispatcherDeliveryPointOperationEvents;
  currentPhases: i18nDispatcherDeliveryPointCurrentPhases;
};

export type i18nDispatcherDeliveryPointCurrentPhases = {
  title: string;
  current: {
    title: string;
    currentPhase1: string;
    currentPhase2: string;
    currentPhase3: string;
  };
  voltage: {
    title: string;
    voltagePhase1: string;
    voltagePhase2: string;
    voltagePhase3: string;
  };
  power: {
    title: string;
    cosPhi: string;
    frequency: string;
    activePower: string;
  };
};

export type i18nDispatcherDeliveryPointOperationEvents = {
  title: string;
};

export type i18nDispatcherDeliveryPointSystemValuesState = {
  heartbeat: string;
  heartbeatLastUpdate: string;
  operationEvent: string;
  centreBreakDisconnectorStatus: string;
  fuseCarrierStatus: string;
  outputBreakerStatus: string;
  powerSwitchStatus: string;
  groundingStatus: string;
  hrmStatus: string;
  activeEnergyTotalSupply: string;
  activeEnergyTotalOfftake: string;
  sourceType: {
    FIELD: string;
    DIST: string;
    DPOINT: string;
  };
};

export type i18nDispatcherOnlineDispatchingDevice = {
  deviceParameters: i18nDispatcherDeviceParameters;
  deviceControlMode: {
    max_power: string;
    requested_power: string;
  };
  activeAndReactivePowerProgress: i18nDispatcherDeviceActiveAndReactivePowerProgress;
};

export type i18nDispatcherDeviceParameters = {
  stateOfCharge: string;
  activeEnergySupply: string;
  activeEnergyOfftake: string;
  phaseElementStatus: string;
  activeEnergyTotalSupply: string;
  activeEnergyTotalOfftake: string;
  deviceControlMode: string;
  dcVoltage: string;
  dcCurrent: string;
  deviceEfficiency: string;
  requestedPower: string;
  activePower: string;
  reactivePower: string;
  tankFillLevel: string;
  lowerWaterLevel: string;
  upperWaterLevel: string;
  turbineWaterFlow: string;
  passiveWaterFlow: string;
};

export type i18nDispatcherDeviceActiveAndReactivePowerProgress = {
  title: string;
  activePower: string;
  reactivePower: string;
  activeRequestedPower: string;
};

export type i18nDispatcherCodebooks = {
  LOSS_OF_POWER_SUPPLY_VOLTAGE: i18nInput;
  BATTERY_COMMUNICATION_MALFUNCTION: i18nInput;
  BATTERY_CHARGING_FAILURE: i18nInput;
  BATTERY_MALFUNCTION: i18nInput;
  LOSS_OF_SIGNAL_AND_CONTROL_VOLTAGE: i18nInput;
  OOM_METER_COMMUNICATION_FAILURE: i18nInput;
  DEVICE_METER_COMMUNICATION_FAILURE: i18nInput;
  LOCAL_DELIVERY_POINT_CONTROL: i18nInput;
  HRM_BLOCKED_BY_DISTRIBUTOR: i18nInput;
  PROTECTOR_FAILURE: i18nInput;
  PROTECTOR_ACTION: i18nInput;
  FUSE_BLOWN: i18nInput;
  DEVICE_FAILURE: i18nInput;
  0: i18nInput;
  1: i18nInput;
  2: i18nInput;
  3: i18nInput;
  ONLINE: i18nInput;
  OFFLINE: i18nInput;
  AIEMS_OPERATION: i18nInput;
  LOCAL_OPERATION: i18nInput;
  UNKNOWN: i18nInput;
  P_MODE: i18nInput;
  Q_MODE: i18nInput;
  WATER_LEVEL_MODE: i18nInput;
};

export type i18nDispatcherProductionPlanning = {
  title: string;
  csvPrefix: string;
  systemValues: {
    title: string;
    [MetricValueType.price15mD1]: string;
    [MetricValueType.price60mD1]: string;
    [MetricValueType.priceDamD1]: string;
    [MetricValueType.predictionSystemImbalanceD1]: string;
    price: string;
    imbalance: string;
  };

  balanceGroupDiagram: {
    title: string;
    [MetricValueType.plannedImbalanceD1]: string;
    [MetricValueType.currentImbalanceD1]: string;
    [MetricValueType.plannedImbalancePrice]: string;
    [MetricValueType.energyProdNomination]: string;
    [MetricValueType.predProdEnergyProdD1]: string;
    [MetricValueType.energyProdSummaryProduction]: string;
  };
  businessDay: {
    title: string;
    step01: string;
    step02: string;
    step03: string;
  };
  productionPlan: {
    productionPlanTitle: string;
    consumptionPlanTitle: string;
    revisionNr: string;
    productionPlanPeriodDuration: {
      [MarketPeriodsType.MARKET_PERIODS]: string;
      [MarketPeriodsType.MARKET_HOURS]: string;
    };
    changeNominationToSummaryProduction: string;
    nomination: string;
    productionSummary: string;
    revisionHistory: string;
    sendToOKTE: string;
    showPrediction: string;
    deviceProductionPlan: i18nDispatcherProductionPlanningDevice;
    [MetricValueType.predProdEndOfPeriodSocD1]: string;
    [MetricValueType.predProdEndOfPeriodEnergyStoredD1]: string;
    [MetricValueType.predProdEndOfPeriodFreeCapacityD1]: string;
    [MetricValueType.predProdEnergyProdD1]: string;
    [MetricValueType.predProdEndOfPeriodPowerD1]: string;
  };
  dailyDiagram: i18nDispatcherDailyDiagram;
  productionPlanRevisionStatus: {
    [PlanRevisionStatus.DRAFT]: string;
    [PlanRevisionStatus.CONFIRMED]: string;
    [PlanRevisionStatus.PROPAGATED]: string;
    [PlanRevisionStatus.PLANNED]: string;
    [PlanRevisionStatus.REJECTED]: string;
    [PlanRevisionStatus.NEW]: string;
    [PlanRevisionStatus.ACCEPTED]: string;
  };
  revision: i18nRevision;
  activityLog: i18nDispatcherActivityLog;
  input: i18nDispatcherProductionPlanningInputs;
  validation: i18nDispatcherProductionPlanningValidation;
};

type i18nDispatcherProductionPlanningDevice = {
  confirm: string;
  edit: string;
  propagate: string;
  showRevisionHistory: string;
  columns: {
    productionPlan: string;
    requestedProduction: string;
    predictedProduction: string;
  };
};

type i18nDispatcherOperationCalendar = {
  title: string;
};

type i18nDispatcherActivityLog = {
  title: string;
};

type i18nDispatcherDailyDiagram = {
  createDailyNomination: string;
  updateDailyNomination: string;
  acceptDailyNomination: string;
  showHistoryOfDailyNomination: string;
  historyOfDailyNomination: string;
};

type i18nRevision = {
  revision: string;
  status: string;
  totalProduction: string;
  createdBy: string;
  createdAt: string;
  revisionHistory: string;
  currentRevision: string;
  copyRevision: string;
  copyRevisionDescription: string;
  confirmCopy: string;
  confirmCopyRevision: string;
  confirmApply: string;
  confirmRevision: string;
  confirmConfirm: string;
  propagateRevision: string;
  confirmPropagate: string;
};

type i18nDispatcherProductionPlanningInputs = {
  filter: string;
};

type i18nDispatcherProductionPlanningValidation = {
  [DailyDiagramValidationReason.MISSING_PRODUCTION_PLAN_REVISION]: string;
  [DailyDiagramValidationReason.PRODUCTION_PLAN_REVISION_OUT_OF_DAILY_DIAGRAM]: string;
  [DailyDiagramValidationReason.PRODUCTION_PLAN_REVISION_IN_DRAFT_STATE]: string;
  [DailyDiagramValidationReason.NEWER_REVISION_FOR_DRAFT_PRODUCTION_PLAN_REVISION_EXISTS]: string;
};

export type i18nDispatcherWeatherWidget = {
  titleCurrentWeather: string;
  titlePredictionWeather: string;
  airTemperature: string;
  measuredAirTemperature: string;
  extendedWeatherForecast: string;
  weather: string;
  period: string;
  maxMinTooltip: string;
  weatherForecast: i18nDispatcherWeatherForecast;
  weatherMeasurementHistory: i18nDispatcherWeatherMeasurementHistory;
};

export type i18nDispatcherWeatherForecast = {
  title: string;
  columns: {
    period: string;
    airTemperature: string;
    windSpeed10m: string;
    windSpeed100m: string;
    windDirection10m: string;
    windDirection100m: string;
    globalHorizontalIrradiance: string;
    globalHorizontalIrradiance10: string;
    globalHorizontalIrradiance90: string;
    diffuseHorizontalIrradiance: string;
    diffuseHorizontalIrradiance10: string;
    diffuseHorizontalIrradiance90: string;
    directNormalIrradiance: string;
    globalTiltedIrradiance: string;
    globalTiltedIrradiance10: string;
    globalTiltedIrradiance90: string;
    cloudOpacity: string;
  };
};

export type i18nDispatcherWeatherMeasurementHistory = {
  title: string;
  columns: {
    period: string;
    airTemperature: string;
    windSpeed: string;
    windDirection: string;
    globalHorizontalIrradiance: string;
    atmosphericPressure: string;
    relativeHumidity: string;
    dewPointTemperature: string;
    rainfallRate: string;
    waterSnowEquivalent: string;
  };
};

export type i18nDispatcherHxDashboard = {
  devicePanel: {
    title: string;
    selectDevice: {
      title: string;
      [DeviceRole.CONSUMPTION]: string;
      [DeviceRole.PRODUCTION]: string;
      [ConsumptionDeviceType.CONS]: string;
      [ProductionDeviceType.FVE]: string;
      [ProductionDeviceType.BESS]: string;
      [ProductionDeviceType.MVE]: string;
      [ProductionDeviceType.KGJ]: string;
    };
    chart: {
      label: {
        x: string;
        y: string;
      };
      currentPlan: string;
      nomination: string;
    };
  };
  hxPlanning: {
    header: {
      filterCaptionLabel: string;
      columns: {
        [HxPlanningDeviceColumn.NOMINATION]: string;
        [HxPlanningDeviceColumn.CURRENT_PLAN]: string;
        [HxPlanningDeviceColumn.PREDICTION]: string;
        [HxPlanningDeviceColumn.SOC_MWH]: string;
      };
    };
    tables: {
      summary: {
        time: string;
        estimatedSI: {
          title: string;
          direction: string;
          expectationSI: string;
          isp: string;
        };
        imbalance: {
          title: string;
          plan: string;
          currentPlan: string;
          paymentForImbalance: string;
        };
        deliveryPrice: {
          title: string;
          nomination: string;
          currentPlan: string;
        };
      };
      device: {
        actions: {
          [HxPlanningContentDeviceHeaderAction.DIRECT_CONFIRMATION]: string;
          [HxPlanningContentDeviceHeaderAction.PROPAGATION]: string;
          [HxPlanningContentDeviceHeaderAction.DIRECT_PROPAGATION]: string;
          [HxPlanningContentDeviceHeaderAction.SET_PREDICTION_TO_PRODUCTION]: string;
        };
        header: {
          [HxPlanningDeviceColumn.NOMINATION]: string;
          [HxPlanningDeviceColumn.CURRENT_PLAN]: string;
          [HxPlanningDeviceColumn.PREDICTION]: string;
          [HxPlanningDeviceColumn.SOC_MWH]: {
            soc: string;
            mwh: string;
          };
        };
      };
    };
  };
  siPanel: {
    title: string;
    chart: {
      yaxisSystemImbalanceLabel: string;
      yaxisSubjectImbalanceLabel: string;
      predictionSystemImbalanceHXChartPoints: string;
      energyImbalanceChartPoints: string;
      actualSystemImbalanceChartPoints: string;
    };
    table: {
      headerLabel: string;
      groupedColumnsLabel: string;
      positiveSystemImbalanceProbability: string;
    };
  };
};

export type i18nDispatcherRkDashboard = {
  mdsPanel: {
    title: string;
    reservedCapacityGuardMode: {
      [ReservedCapacityGuardMode.AUTO]: string;
      [ReservedCapacityGuardMode.MANUAL]: string;
    };
    energyOfftakeChart: {
      label: {
        x: string;
        y: string;
      };
      series: {
        [MetricValueType.activeEnergyOfftake]: string;
        [MetricValueType.energyPlannedProduction]: string;
        reservedCapacityMaintainLevel: string;
        reservedCapacityFulfillment: string;
        reservedCapacityFulfillmentWithReserve: string;
      };
    };
    activeEnergyChart: {
      title: string;
      label: {
        x: string;
        y: string;
      };
      restrictionPrevention: string;
      series: {
        [MetricValueType.activeEnergyOfftake]: string;
        [MetricValueType.energyMaxPower]: string;
        maxPowerOfftakeInPreventivePeriodPhase: string;
        reservedCapacity: string;
        reservedCapacityReserve: string;
      };
    };
    alarms: {
      title: string;
      removeAll: string;
      openAlarms: string;
    };
  };
  rkPanel: {
    header: {
      currentConsumptionRk: string;
      elapsedTime: string;
      reservedCapacity: string;
      newSuggestions: string;
      checkingInProgress: string;
    };
    columns: {
      [PanelRkColumn.OOM]: string;
      [PanelRkColumn.DEVICE]: string;
      [PanelRkColumn.PRIORITY]: string;
      [PanelRkColumn.CURRENT_POWER]: string;
      [PanelRkColumn.CONSUMED_POWER]: string;
      [PanelRkColumn.ESTIMATED_CONSUMPTION]: string;
      [PanelRkColumn.SUGGESTED_CONSUMPTION]: string;
      [PanelRkColumn.REQUIRED_CONSUMPTION]: string;
      [PanelRkColumn.START_OF_MEASUREMENT]: string;
      [PanelRkColumn.ISSUANCE_OF_MEASUREMENT]: string;
      [PanelRkColumn.LEAVE_FOR_LATER]: string;
      [PanelRkColumn.UNIT_PRICE_OF_ENERGY]: string;
      [PanelRkColumn.DEVIATION_ESTIMATE]: string;
      [PanelRkColumn.NOMINATION]: string;
    };
    message: {
      priorityValidationError: string;
    };
    changeGuardingActionDialog: {
      title: string;
      startOfMeasurement: string;
      plusInterval: string;
      minusInterval: string;
      now: string;
      estimatedConsumptionTotal: string;
      estimatedConsumptionTotalValue: string;
      reservedCapacity: string;
      reservedCapacityValue: string;
      estimatedConsumptionDevice: string;
      estimatedConsumptionDeviceValue: string;
      leaveForLater: string;
      cancelAction: string;
      acceptActionWithChange: string;
    };
  };
  guardingActions: {
    proposedGuardingActions: {
      title: string;
      onlyCurrent: string;
      onlyIssued: string;
      deliveryPointsSelect: string;
      dialog: {
        accept: {
          title: string;
          description: string;
        };
        acceptBatch: {
          title: string;
          description: string;
        };
        reject: {
          title: string;
          description: string;
        };
      };
    };
    proposedGuardingActionsTable: {
      proposalTime: string;
      deviceName: string;
      guardingAction: string;
      acceptTime: string;
      action: string;
    };
    acceptedGuardingActions: {
      title: string;
      planned: string;
      applied: string;
      deliveryPointsSelect: string;
    };
    acceptedGuardingActionsTable: {
      proposalTime: string;
      deviceName: string;
      guardingAction: string;
      acceptTime: string;
      type: string;
    };
    actionReduction: string;
    actionIncrease: string;
  };
  activityLog: {
    title: string;
  };
};

// module - eai
export type i18nEaiUsers = {
  userDetail: i18nEaiUserDetail;
  createUserWizard: i18nEaiCreateUser;
  userList: i18nEaiUserList;
  input: i18nEaiUserInputs;
  validation: i18nEaiUserValidation;
};

type i18nEaiUserDetail = {
  userProfile: {
    title: string;
    basicInfo: string;
    accountStateSwitch: string;
    accountState: {
      [UserAccountStatus.ENABLED]: string;
      [UserAccountStatus.DISABLED]: string;
    };
    resetPassword: string;
    deleteUser: string;
  };
  userAssignment: {
    title: string;
    columns: i18nEaiUserAssignmentColumns;
    addMemberAccount: string;
    rolesAssignment: i18nEaiUserRolesAssignment;
  };
};

type i18nEaiUserAssignmentColumns = {
  subjectAccount: string;
  accountType: string;
  memberAccountStatus: string;
  roles: string;
  actions: string;
};

type i18nEaiUserRolesAssignment = {
  columns: {
    role: string;
    validFromDate: string;
    actions: string;
  };
  addRole: string;
};

type i18nUserAccountState = {
  description: {
    [UserAccountStatus.ENABLED]: string;
    [UserAccountStatus.DISABLED]: string;
  };
  submitButtonTitle: {
    [UserAccountStatus.ENABLED]: string;
    [UserAccountStatus.DISABLED]: string;
  };
};

type i18nRemoveEntityOrRelationDialog = {
  title: string;
  description: string;
  submitButtonTitle: string;
};

type i18nResetPasswordDialog = {
  description: string;
  submitButtonTitle: string;
};

type i18nDeleteSubjectDialog = {
  description: string;
  submitButtonTitle: string;
};

type i18nEaiCreateUser = {
  title: string;
  step01: string;
  save: string;
};

type i18nEaiUserList = {
  title: string;
  searchInput: string;
  createEntityButton: string;
  columns: i18nEaiUserListColumns;
  userActions: i18nEaiUserActions;
};

type i18nEaiUserListColumns = {
  username: string;
  firstname: string;
  lastname: string;
  status: string;
};

type i18nEaiUserActions = {
  detail: string;
  enable: string;
  disable: string;
};

export type i18nEaiSubjects = {
  createSubjectWizard: i18nEaiCreateSubject;
  subjectDetail: i18nEaiSubjectDetail;
  subjectList: i18nEaiSubjectList;
  input: i18nEaiSubjectInputs;
  validation: i18nEaiSubjectValidations;
};

type i18nEaiCreateSubject = {
  title: string;
  step01: string;
  step02: string;
  step03: string;
  step04: string;
  save: string;
};

type i18nEaiSubjectDetail = {
  userData: {
    title: string;
    basicInfo: string;
    deleteSubject: string;
  };
  userAccount: {
    title: string;
    account: string;
    accountState: {
      [EMSMemberAccountStatus.ACTIVE]: string;
      [EMSMemberAccountStatus.INACTIVE]: string;
    };
    serviceLevelCodeHistory: string;
    serviceLevelSpecificationId: string;
  };
  marketParticipantData: {
    title: string;
    marketParticipantCredentials: string;
    basicInfo: string;
    type: string;
    username: string;
    actions: string;
    addCredentials: string;
  };
  createSubjectAccountWizard: {
    title: string;
    step01: string;
    step02: string;
    step03: string;
    save: string;
  };
};

type i18nEaiSubjectList = {
  title: string;
  searchInput: string;
  createEntityButton: string;
  columns: i18nEaiSubjectListColumns;
  filter: i18nEaiSubjectListFilter;
};

type i18nEaiSubjectListFilter = {
  status: string;
  levelCode: string;
  validity: string;
};

type i18nEaiSubjectListColumns = {
  name: string;
  partyIdentifier: string;
  eic: string;
  serviceLevel: string;
  status: string;
  validTo: string;
};

export type i18nEaiBalanceGroups = {
  balanceGroupList: i18nEaiBalanceGroupList;
  balanceGroupData: {
    title: string;
    basicInfo: string;
  };
  balanceGroupProperties: {
    title: string;
    deviationSubCardTitle: string;
    limit: string;
    period: string;
    optimizedPeriodNumberSelectLabel: string;
    productionModelTitle: string;
  };
  balanceGroupDeliveryPointList: {
    title: string;
    columns: {
      name: string;
      eic: string;
      id: string;
      actions: string;
    };
  };
  input: {
    eic: string;
    name: string;
    emsPartyName: string;
  };
  validation: i18nEaiBalanceGroupValidation;
};

type i18nEaiBalanceGroupList = {
  searchInput: string;
  columns: i18nEaiBalanceGroupListColumns;
};

type i18nEaiBalanceGroupListColumns = {
  name: string;
  eic: string;
  emsPartyName: string;
};

type i18nEaiBalanceGroupValidation = {
  'name-required': string;
  'eic-required': string;
  'eic-length': string;
  'eic-available': string;
};

export type i18nEaiDevices = {
  createDeviceWizard: i18nEaiCreateDevice;
  deviceList: i18nEaiDeviceList;
  input: i18nEaiDeviceInputs;
  validation: i18nEaiDeviceValidations;
  deviceDetail: i18nEaiDeviceDetail;
};

type i18nEaiCreateDevice = {
  title: string;
  step01: string;
  step02: string;
  save: string;
};

type i18nEaiDeviceList = {
  searchInput: string;
  createEntityButton: string;
  columns: i18nEaiDeviceListColumns;
  filter: i18nEaiDeviceListFilter;
};

type i18nEaiDeviceListFilter = {
  status: string;
  deliveryPointName: string;
};

type i18nEaiDeviceListColumns = {
  deviceId: string;
  name: string;
  deliveryPointName: string;
  installationStatus: string;
};

type i18nEaiDeviceInputs = {
  DeviceName: string;
  DeviceRole: string;
  ProductionDeviceType: string;
  DeviceDeliveryPoint: string;
  DeviceId: string;
  DeviceDescription: string;
  DeviceCharacteristicGroupName: string;
  DeviceCharacteristicGroupDescription: string;
  DeliveryPointName: string;
  GatewayOfDispatcherSystem: string;
  ServiceCode: string;
};

type i18nEaiDeviceValidations = {
  'DeviceName-required': string;
  'DeviceRole-required': string;
  'ProductionDeviceType-required': string;
  'DeviceDeliveryPoint-required': string;
  'DeviceName-max': string;
  'DeviceCharacteristicGroupName-required': string;
  'DeviceCharacteristicGroupName-length': string;
};

type i18nEaiDeviceDetail = {
  deviceData: {
    title: string;
    basicInfo: string;
    installationStatusSwitch: string;
    installationStatus: {
      [DeviceInstallationStatus.INSTALLED]: string;
      [DeviceInstallationStatus.NOT_INSTALLED]: string;
    };
    cancelGDSAssignmentValue: string;
  };
  deviceCharacteristics: {
    title: string;
    criticalCharacteristicsTitle: string;
    nonCriticalCharacteristicsTitle: string;
    deviceTechnicalCharacteristics: {
      title: string;
      input: {
        panel_type: string;
        panel_count: string;
        panel_area: string;
        panel_efficiency: string;
        panel_degradation: string;
        panel_degradation_y1: string;
        empirical_coefficient_u0: string;
        empirical_coefficient_u1: string;
        max_power_output: string;
        tkmo: string;
        converter_efficiency: string;
        cssc: string;
        feeder_losses: string;
        stc_irradiation: string;
        stc_temperature: string;
        notc_irradiation: string;
        notc_temperature: string;
        latitude: string;
        longitude: string;
        elevation: string;
        panel_azimuth: string;
        panel_tilt: string;
        correction_coefficient: string;
        battery_type: string;
        battery_capacity_available_percentage: string;
        rated_charge: string;
        rated_discharge: string;
        max_charge: string;
        max_discharge: string;
        energy_availability_bess: string;
        energy_availability_bess_50: string;
        usable_energy: string;
        charge_cycles: string;
        bat_life_expectancy: string;
        bat_efficiency: string;
        bat_degradation_level: string;
        soc: string;
        min_soc_end_condition: string;
        max_soc_end_condition: string;
        min_soc: string;
        max_soc: string;
        temperature_range_from: string;
        temperature_range_to: string;
        charging_characteristic: string;
        discharge_characteristic: string;
        pmax_consumption: string;
        pmin_consumption: string;
        min_power_change_duration: string;
        poweroutput_change_type: string;
        consumption_change_unitcost: string;
        degree_of_consumption_power_change_in_stages: string;
        poweroutput_rate_of_change: string;
        min_poweroutput_rate_of_change: string;
        max_poweroutput_rate_of_change: string;
        degree_of_consumption_power_change: string;
        chart_characteristic_capacity: string;
        chart_characteristic_poweroutput: string;
      };
      validation: {
        'panel_count-number': string;
        'charge_cycles-number': string;
        'value-number-range': string;
        'value-number': string;
        'latitude-number': string;
        'longitude-number': string;
        'elevation-number': string;
        'charging_characteristic-value': string;
        'discharge_characteristic-value': string;
        'value-required': string;
        'pmax_consumption-required': string;
        'pmax_consumption-value': string;
        'pmin_consumption-required': string;
        'pmin_consumption-value': string;
        'poweroutput_change_type-required': string;
        'consumption_change_unitcost-required': string;
        'min_soc_end_condition-value': string;
        'max_soc_end_condition-value': string;
        'min_soc-value': string;
        'max_soc-value': string;
        'min_poweroutput_rate_of_change-value': string;
        'max_poweroutput_rate_of_change-value': string;
      };
      charging_characteristic: {
        title: string;
        input: {
          batteryCapacity: string;
          chargingPower: string;
        };
        validation: {
          'chargingPower-required': string;
          'chargingPower-number': string;
          'batteryCapacity-required': string;
        };
        batteryCapacity: string;
        chargingPower: string;
        actions: string;
      };
      discharge_characteristic: {
        title: string;
        input: {
          batteryCapacity: string;
          dischargePower: string;
        };
        validation: {
          'dischargePower-required': string;
          'dischargePower-number': string;
          'batteryCapacity-required': string;
        };
        batteryCapacity: string;
        dischargePower: string;
        actions: string;
      };
      power_change_characteristic: {
        title: string;
        input: {
          consumptionPowerDegree: string;
          changeDuration: string;
        };
        consumptionPowerDegree: string;
        changeDuration: string;
        actions: string;
      };
    };
    production_model: {
      title: string;
    };
    deviceEconomicalCharacteristics: {
      title: string;
      input: {
        capex: string;
        opex: string;
        electricity_levelized_cost: string;
        system_estimated_lifetime: string;
        annual_produced_energy: string;
        discount_factor: string;
        storage_discounted_levelized_cost: string;
      };
      validation: {
        'capex-number': string;
        'opex-number': string;
      };
    };
    deviceWeatherCharacteristics: {
      title: string;
      externalWeatherMonitoringTooltip: string;
      input: {
        external_weather_monitoring: string;
        solcast_resource_id: string;
        latitude: string;
        longitude: string;
      };
      validation: {
        'latitude-number': string;
        'longitude-number': string;
        'solcast_resource_id-match': string;
      };
    };
    deviceReservedCapacityGuardingCharacteristics: {
      title: string;
      input: {
        [CharacteristicsRk.reserved_capacity_guard_enabled]: string;
        [CharacteristicsRk.reserved_capacity_guard_priority]: string;
        [CharacteristicsRk.reserved_capacity_max_restriction_duration]: string;
        [CharacteristicsRk.reserved_capacity_max_reduction_limit]: string;
        [CharacteristicsRk.reserved_capacity_max_reduction_limit_type]: string;
        [CharacteristicsRk.reserved_capacity_max_change_of_rate_of_power_change_reduction]: string;
      };
      validation: {
        [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_GUARD_PRIORITY_REQUIRED]: string;
        [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_GUARD_PRIORITY_NUMBER]: string;
        [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_RESTRICTION_DURATION_REQUIRED]: string;
        [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_RESTRICTION_DURATION_NUMBER]: string;
        [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_REDUCTION_LIMIT_REQUIRED]: string;
        [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_REDUCTION_LIMIT_NUMBER]: string;
        [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_REDUCTION_LIMIT_TYPE_REQUIRED]: string;
        [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_CHANGE_OF_RATE_OF_POWER_CHANGE_REDUCTION_REQUIRED]: string;
        [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_CHANGE_OF_RATE_OF_POWER_CHANGE_REDUCTION_NUMBER]: string;
      };
    };
    unitsOfMeasureLabels: {
      SQUARE_METRE: string;
      SQUARE_MILIMETRE: string;
      PERCENT: string;
      WATT_PEAK: string;
      EUR: string;
      EUR_PER_KILOWATT_HOUR: string;
      EUR_PER_MEGAWATT_HOUR: string;
      DEGREE_CELSIA: string;
      WATT_PER_SQUARE_METRE: string;
      DEGREE: string;
      METRE: string;
      KWH: string;
      KW: string;
      MW: string;
      WATT: string;
      SQUARE_METRE_CUBED: string;
      SECONDS: string;
      EUR_PER_HOUR: string;
      KW_PER_SECOND: string;
      PERCENT_PER_DEGREE_CELSIA: string;
      YEARS: string;
    };
  };
  createDeviceCharacteristicGroupWizard: {
    title: string;
    step01: string;
    step02: string;
    save: string;
  };
};

export type i18nEaiDeliveryContracts = {
  deliveryContractDetail: i18nEaiDeliveryContractDetail;
  createDeliveryContractWizard: i18nEaiCreateDeliveryContract;
  deliveryContractList: i18nEaiDeliveryContractList;
  input: i18nEaiDeliveryContractInputs;
  validation: i18nEaiDeliveryContractValidation;
};

type i18nEaiDeliveryContractList = {
  title: string;
  searchInput: string;
  createEntityButton: string;
  columns: i18nEaiDeliveryContractListColumns;
  filter: i18nEaiContractListFilter;
};

type i18nEaiContractListFilter = {
  validityFrom: string;
  validityTo: string;
  status: string;
  type: string;
};

type i18nEaiDeliveryContractListColumns = {
  id: string;
  code: string;
  type: string;
  name: string;
  startDate: string;
  endDate: string;
  status: string;
};

type i18nEaiDeliveryContractDetail = {
  contractData: {
    title: string;
    basicInfo: string;
    contractType: string;
  };
  deliveryPoints: {
    title: string;
    columns: i18nEaiDeliveryContractDeliveryPointColumns;
    addDeliveryPoint: string;
  };
};

type i18nEaiCreateDeliveryContract = {
  title: string;
  step01: string;
  step02: string;
  save: string;
};

type i18nEaiDeliveryContractInputs = {
  ProducerMarketParticipant: string;
  ConsumerMarketParticipant: string;
  DeliveryContractType: string;
  Amount: string;
  Unit: string;
  UnitPrice: string;
  IndexPriceRatio: string;
  StartDate: string;
  EndDate: string;
  Name: string;
  Id: string;
  Description: string;
  Code: string;
  DeliveryPoint: string;
};

type i18nEaiDeliveryContractDeliveryPointColumns = {
  deliveryPointId: string;
  name: string;
  eic: string;
  actions: string;
};

type i18nEaiDeliveryContractValidation = {
  'ProducerMarketParticipant-required': string;
  'ConsumerMarketParticipant-required': string;
  'DeliveryContractType-required': string;
  'Amount-required': string;
  'Amount-number': string;
  'Unit-required': string;
  'UnitPrice-required': string;
  'UnitPrice-number': string;
  'IndexPriceRatio-required': string;
  'IndexPriceRatio-number': string;
  'IndexPriceRatio-max': string;
  'StartDate-required': string;
  'StartDate-beforeEndDate': string;
  'EndDate-required': string;
  'EndDate-afterStartDate': string;
  'Name-required': string;
  'Name-length': string;
  'Code-required': string;
  'Code-length': string;
  'Description-length': string;
};

type i18nEaiUserInputs = {
  email: string;
  firstName: string;
  lastName: string;
  emsMemberAccountId: string;
  role: string;
  username: string;
};

type i18nEaiUserValidation = {
  'email-required': string;
  'email-matches': string;
  'email-exists': string;
  'firstName-required': string;
  'lastName-required': string;
  'emsMemberAccountId-required': string;
};

type i18nEaiSubjectInputs = {
  EMSPartyName: string;
  EMSPartyType: string;
  EMSPartyIdentifier: string;
  EMSMemberAccountType: string;
  ServiceLevelCode: string;
  EnergyMarketParticipantRole: string;
  ImbalanceResponsibilityMode: string;
  EMSMemberAccountName: string;
  EMSMemberAccountEic: string;
  username: string;
  password: string;
  clientCertificateFile: string;
  uploadClientCertificateFile: string;
  certificateMaxSize: string;
  fileSize: string;
  fileSizeBytes: string;
  MarketParticipantCredentialsType: string;
};

type i18nEaiSubjectValidations = {
  'EMSPartyName-required': string;
  'EMSPartyName-max': string;
  'EMSPartyName-mail': string;
  'EMSPartyName-exists': string;
  'EMSPartyType-required': string;
  'EMSPartyIdentifier-required': string;
  'EMSPartyIdentifier-matches': string;
  'EMSPartyIdentifier-exists': string;
  'EMSMemberAccountName-required': string;
  'EMSMemberAccountType-required': string;
  'EnergyMarketParticipantRole-required': string;
  'ServiceLevelCode-required': string;
  'ImbalanceResponsibilityMode-required': string;
  'EMSMemberAccountEic-required': string;
  'EMSMemberAccountEic-max': string;
  'EMSMemberAccountEic-exists': string;
  'username-required': string;
  'password-required': string;
  'clientCertificateFile-required': string;
  'MarketParticipantCredentialsType-required': string;
};

export type i18nEaiCodebooks = {
  INDIVIDUAL: i18nInput;
  ORGANISATION: i18nInput;
  PRODUCER: i18nInput;
  CONSUMER: i18nInput;
  PROSUMER: i18nInput;
  SUPPLIER: i18nInput;
  AGGREGATOR: i18nInput;
  ELECTRICITY_STORAGE_OPERATOR: i18nInput;
  ENERGY_COMMUNITY: i18nInput;
  BUYER: i18nInput;
  OWN: i18nInput;
  TRANSFERRED: i18nInput;
  MARKET_PARTICIPANT: i18nInput;
  SERVICE_PROVIDER: i18nInput;
  LEVEL_0: i18nInput;
  LEVEL_1: i18nInput;
  LEVEL_2: i18nInput;
  PRODUCTION_OPTIMIZATION: i18nInput;
  PRODUCTION_OPTIMIZATION_DELIVERY_POINT: i18nInput;
  PRODUCTION_OPTIMIZATION_BALANCING_GROUP: i18nInput;
  RESERVED_CAPACITY_GUARDING: i18nInput;
  DEVICE_PRODUCTION_PLAN_DAILY: i18nInput;
  DEVICE_PRODUCTION_PLAN_HOURLY: i18nInput;
  ACTIVE: i18nInput;
  INACTIVE: i18nInput;
  BLOCKED: i18nInput;
  ENABLED: i18nInput;
  DISABLED: i18nInput;
  OKTE_ISZO: i18nInput;
  OKTE_ISOT: i18nInput;
  DAMAS: i18nInput;
  SUPPLY_POINT: i18nInput;
  OFFTAKE_POINT: i18nInput;
  LOCAL_DISTRIBUTION_SYSTEM: i18nInput;
  INSTALLED: i18nInput;
  NOT_INSTALLED: i18nInput;
  PRODUCTION: i18nInput;
  CONSUMPTION: i18nInput;
  FVE: i18nInput;
  BESS: i18nInput;
  MVE: i18nInput;
  KJG: i18nInput;
  ENTIRE_AVAILABLE_CONSUMPTION_POWER_SWITCH: i18nInput;
  AVAILABLE_CONSUMPTION_POWER_STAGES_SWITCH: i18nInput;
  LINEAR_CONTINUOUS_CONSUMPTION_POWER_CHANGE_FIXED: i18nInput;
  LINEAR_CONTINUOUS_CONSUMPTION_POWER_CHANGE_ADJUSTABLE: i18nInput;
  FIXED_CONSUMPTION_PROFILE: i18nInput;
  NONLINEAR_CONTINUOUS_CONSUMPTION_POWER_CHANGE: i18nInput;
  AMORPHOUS: i18nInput;
  POLYCRYSTALLINE: i18nInput;
  MONOCRYSTALLINE: i18nInput;
  REDOX: i18nInput;
  LITHIUM: i18nInput;
  FIXED_PRICE: i18nInput;
  INDEXED_PRICE: i18nInput;
  VALID: i18nInput;
  NOT_VALID: i18nInput;
  DELETED: i18nInput;
  NOT_DELETED: i18nInput;
  ALL: i18nInput;
};

export type i18nEaiMessages = {
  changeEmsMemberAccountSubscription: string;
  createEmsParty: string;
  updateEmsParty: string;
  deleteEmsParty: string;
  createEmsMemberAccount: string;
  updateEmsMemberAccount: string;
  activateEmsMemberAccount: string;
  deactivateEmsMemberAccount: string;
  updateMarketParticipant: string;
  addMarketParticipantCredentials: string;
  removeMarketParticipantCredentials: string;
  createEmsMemberAccountUser: string;
  createDeliveryPoint: string;
  updateDeliveryPoint: string;
  updateDeliveryPointCharacteristics: string;
  updateDeliveryPointGDSName: string;
  enableUserAccount: string;
  disableUserAccount: string;
  resetUserPassword: string;
  updateUser: string;
  deleteUser: string;
  assignEmsMemberAccountUser: string;
  unassignEmsMemberAccountUser: string;
  addUserRole: string;
  deleteUserRole: string;
  updateBalanceGroup: string;
  updateBalanceGroupCharacteristics: string;
  createDevice: string;
  updateDevice: string;
  assignGDS: string;
  registerGDSToDeliveryPoint: string;
  unassignGDSFromDeliveryPoint: string;
  updateDeviceCharacteristicGroupCharacteristics: string;
  createDeviceCharacteristicGroup: string;
  deleteDevice: string;
  deleteDeviceGroup: string;
  createDeliveryContract: string;
  updateDeliveryContract: string;
  assignDeliveryPoint: string;
  assignDeliveryPointChange: string;
  unassignDeliveryPoint: string;
  updateOperationCalendarEvent: string;
  updateOperationEvent: string;
  invalidateOperationEvent: string;
  deleteOperationCalendarEvent: string;
};

// crash fallback
type i18nCrashFallback = {
  headline: string;
  message: string;
  messageCopied: string;
  button: i18nCrashFallbackButton;
};

type i18nCrashFallbackButton = {
  copy: string;
  restore: string;
};

// dialog
type i18nDefaultDialog = {
  title: string;
  description: string;
  submitButtonTitle: string;
};

type i18nBusinessTimeDialog = {
  title: string;
  message: string;
};

type i18nSelectEmsMemberAccount = {
  title: string;
  noSubjectDescriptionBegin: string;
  noSubjectLogoutButton: string;
};

type i18nAddCredentials = {
  title: string;
  addCredentialsButton: string;
};

type i18nRemoveCredentials = {
  title: string;
  description: string;
  submitButtonTitle: string;
};

type i18nEmsMemberAccountState = {
  description: {
    [EMSMemberAccountStatus.ACTIVE]: string;
    [EMSMemberAccountStatus.INACTIVE]: string;
  };
  submitButtonTitle: {
    [EMSMemberAccountStatus.ACTIVE]: string;
    [EMSMemberAccountStatus.INACTIVE]: string;
  };
};

type i18nSetDeviceStatusDialog = {
  description: {
    [DeviceInstallationStatus.INSTALLED]: string;
    [DeviceInstallationStatus.NOT_INSTALLED]: string;
  };
  submitButtonTitle: {
    [DeviceInstallationStatus.INSTALLED]: string;
    [DeviceInstallationStatus.NOT_INSTALLED]: string;
  };
};

// common
type i18nUnitsOfMeasure = {
  kWh: string;
  kW: string;
  MWh: string;
  kVAr: string;
  PERCENT: string;
  EUR: string;
  A: string;
  V: string;
  METERS_ABOVE_SEA_LEVEL: string;
  CUBIC_METRE_PER_SECOND: string;
  Hz: string;
  EUR_PER_MEGAWATT_HOUR: string;
  EUR_PER_KILOWATT_HOUR: string;
  DEGREE_CELSIA: string;
  MILLIMETRE: string;
  METRE_PER_SECOND: string;
  Pa: string;
  kW_PER_SQUARE_METRE: string;
  W_PER_SQUARE_METRE: string;
};

type i18nMarketPeriod = {
  [MarketPeriodsType.MARKET_PERIODS]: string;
  [MarketPeriodsType.MARKET_HOURS]: string;
};

type i18nCommonWizard = {
  review: string;
};

type i18nCommonButton = {
  back: string;
  next: string;
  cancel: string;
  saveChanges: string;
  add: string;
  invalidate: string;
  ok: string;
  propagate: string;
  apply: string;
  forceAccept: string;
  edit: string;
  export: string;
  clearSelection: string;
  understand: string;
  login: string;
};

type i18nCommonStatus = {
  ACTIVE: string;
  INACTIVE: string;
  BLOCKED: string;
};

type i18nCommonMessages = {
  caReasons: i18nCaReasonCodes;
  ifNotMatch: string;
  ifNotMatchThen: string;
  forceSave: string;
  or: string;
  discardChanges: string;
  somethingIsWrong: string;
  pageNotFound: string;
  noMenuPermissionsLabel: string;
  noMenuPermissionsText: string;
  noAccessPermissionsForPageLabel: string;
  noAccessPermissionsForPageText: string;
  noPagePermissionsForPageLabel: string;
  noPagePermissionsForPageText: string;
  sessionTimeoutDialogLabel: string;
  sessionTimeoutDialogText: string;
};

type i18nCommonChart = {
  noData: string;
  period: string;
};

type i18nInput = {
  title: string;
  caption?: string;
};

type i18nCreateGDSState = {
  title: string;
  description: string;
  backButtonTitle: string;
  submitButtonTitle: string;
};

type i18nChangeDPAssignment = {
  title: string;
  description: string;
  backButtonTitle: string;
  submitButtonTitle: string;
};

export type i18nCalendar = {
  title: string;
  createEventTitle: string;
  availablePowerForTitle: string;
  titleForContract: string;
  toolbar: {
    todayButton: string;
    viewOptions: {
      month: string;
      week: string;
      day: string;
      agenda: string;
    };
    deviceGroup: string;
    deliveryPointGroup: string;
    balanceGroupsGroup: string;
    showAll: string;
  };
  eventDialog: {
    input: {
      name: string;
      type: string;
      deviceId: string;
      DeviceOffline: string;
      availablePower: string;
      description: string;
      deliveryContractId: string;
      AddButton: string;
      EditButton: string;
      ChooseTime: string;
      invalidateButton: string;
    };
    meta: {
      createdBy: string;
      updatedBy: string;
    };
    validation: {
      'name-required': string;
      'name-max': string;
      'type-required': string;
      'deviceId-required': string;
      'description-max': string;
      'deliveryContractId-required': string;
      'availablePower-required': string;
      'availablePower-number': string;
    };
    AllDay: string;
    NoEvents: string;
    CreateEventTitle: string;
    UpdateEventTitle: string;
    DeviceOffline: string;
    AvailablePower: string;
    StartDate: string;
    EndDate: string;
    EventDescription: string;
    DeleteButton: string;
    InvalidateButton: string;
    CreateEventButton: string;
    SHUTDOWN: string;
    MAINTENANCE: string;
    MALFUNCTION: string;
    CONTRACT: string;
    ErrorStartInPast: string;
    ErrorEndInPast: string;
    ErrorStartTimeMessage: string;
    ErrorEndTimeMessage: string;
    ContractWithoutName: string;
  };
  activityLog: {
    title: string;
  };
  input: {
    Filter: string;
  };
};

export type i18nDispatcherMessages = {
  eventCreated: string;
  errorCreatingEvent: string;
  updateOperationCalendarEvent: string;
  invalidateOperationEvent: string;
  deleteOperationCalendarEvent: string;
  productionPlanCreated: string;
  productionPlanUpdated: string;
  productionPlanPropagated: string;
  productionPlanConfirmed: string;
  productionPlanCopied: string;
  dailyDiagramCreated: string;
  dailyDiagramUpdated: string;
  dailyDiagramAccepted: string;
  dailyDiagramValidationErrors: string;
  dailyDiagramValidationWarningsPrefix: string;
  dailyDiagramValidationWarningsPostfix: string;
  [DispatcherValidationError.MUST_BE_INTEGER]: string;
  [DispatcherValidationError.MAX_2_DECIMALS]: string;
  [DispatcherValidationError.MAX_3_DECIMALS]: string;
  [DispatcherValidationError.MAX_5_DECIMALS]: string;
  [DispatcherValidationError.MAX_6_DECIMALS]: string;
  [DispatcherValidationError.MIN_VALUE]: string;
  [DispatcherValidationError.MAX_VALUE]: string;
  [DispatcherValidationError.MUST_BE_ONE_OF]: string;
  [DispatcherValidationError.NOT_A_NUMBER]: string;
  [DispatcherValidationError.EXPONENTIAL_NUMBER]: string;
  [DispatcherValidationError.POSITIVE_NUMBER]: string;
};

export type i18nEaiDeliveryPoints = {
  createDeliveryPointWizard: {
    title: string;
    step01: string;
    step02: string;
    save: string;
  };
  deliveryPointDetail: {
    basicInfoTab: {
      title: string;
    };
    characteristicsTab: {
      title: string;
      technicalCharacteristics: {
        title: string;
        input: {
          reserved_capacity: string;
          maximum_reserved_capacity: string;
          reserved_capacity_supply: string;
          maximum_reserved_capacity_supply: string;
        };
        validation: {
          'reserved_capacity-required': string;
          'maximum_reserved_capacity-required': string;
          'reserved_capacity-number': string;
          'reserved_capacity-number-range': string;
          'maximum_reserved_capacity-number': string;
          'maximum_reserved_capacity-number-range': string;
        };
      };
      reservedCapacityCharacteristics: {
        title: string;
        input: {
          reserved_capacity_guard_period_start_restriction_offset: string;
          reserved_capacity_guard_phase_one_reserve: string;
          reserved_capacity_guard_phase_two_reserve: string;
          reserved_capacity_guard_earliest_restriction_start_offset: string;
          reserved_capacity_guard_check_periodicity: string;
          reserved_capacity_guard_check_algorithm: string;
          reserved_capacity_utilization_alarm_utilization_threshold: string;
          reserved_capacity_utilization_alarm_time_threshold: string;
        };
        validation: {
          [DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_PERIOD_START_RESTRICTION_OFFSET_REQUIRED]: string;
          [DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_PERIOD_START_RESTRICTION_OFFSET_NUMBER]: string;
          [DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_PHASE_ONE_RESERVE_REQUIRED]: string;
          [DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_PHASE_ONE_RESERVE_NUMBER]: string;
          [DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_PHASE_ONE_RESERVE_MAX]: string;
          [DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_PHASE_TWO_RESERVE_REQUIRED]: string;
          [DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_PHASE_TWO_RESERVE_NUMBER]: string;
          [DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_PHASE_TWO_RESERVE_MAX]: string;
          [DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_EARLIEST_RESTRICTION_START_OFFSET_REQUIRED]: string;
          [DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_EARLIEST_RESTRICTION_START_OFFSET_NUMBER]: string;
          [DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_CHECK_PERIODICITY_REQUIRED]: string;
          [DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_CHECK_ALGORITHM_REQUIRED]: string;
          [DeliveryPointValidationArguments.RESERVED_CAPACITY_UTILIZATION_ALARM_UTILIZATION_THRESHOLD_REQUIRED]: string;
          [DeliveryPointValidationArguments.RESERVED_CAPACITY_UTILIZATION_ALARM_UTILIZATION_THRESHOLD_NUMBER]: string;
          [DeliveryPointValidationArguments.RESERVED_CAPACITY_UTILIZATION_ALARM_TIME_THRESHOLD_REQUIRED]: string;
          [DeliveryPointValidationArguments.RESERVED_CAPACITY_UTILIZATION_ALARM_TIME_THRESHOLD_NUMBER]: string;
        };
      };
    };
    gdsListTab: {
      title: string;
    };
    assignmentsTab: {
      title: string;
    };
    basicInfo: {
      title: string;
      input: {
        DeliveryPointId: string;
        DeliveryPointEic: string;
        DeliveryPointRole: string;
        DeliveryPointName: string;
        DeliveryPointDescription: string;
      };
      validation: {
        'DeliveryPointEic-required': string;
        'DeliveryPointEic-length': string;
        'DeliveryPointEic-exists': string;
        'DeliveryPointName-required': string;
        'DeliveryPointName-max': string;
        'DeliveryPointDescription-max': string;
      };
    };
    basicAddressInfo: {
      title: string;
      input: {
        StreetName: string;
        StreetNumber: string;
        UnitNumber: string;
        PostalCode: string;
        City: string;
      };
      validation: {
        'StreetName-required': string;
        'StreetName-max': string;
        'StreetNumber-required': string;
        'StreetNumber-max': string;
        'UnitNumber-max': string;
        'PostalCode-required': string;
        'PostalCode-length': string;
        'City-required': string;
        'City-max': string;
      };
    };
    gdsList: {
      title: string;
      searchInput: string;
      createEntityButton: string;
      filter: {
        validity: string;
        showOnlyValid: string;
      };
      input: {
        name: string;
        searchString: string;
        showOnlyValid: string;
      };
      columns: {
        id: string;
        name: string;
        validity: string;
        actions: string;
      };
      create: string;
    };
    gdsDetail: {
      searchInput: string;
      createEntityButton: string;
      input: {
        id: string;
        name: string;
        validity: string;
        actions: string;
      };
      basicGdsInfoTab: {
        title: string;
      };
      basicInfo: {
        title: string;
        input: {
          id: string;
          name: string;
          validTo: string;
        };
      };
      gdsDevices: {
        title: string;
        create: string;
        remove: string;
      };
    };
    assignments: {
      changeAssignmentButton: string;
      emsPartyName: string;
      balanceGroupName: string;
      validity: string;
      historyOfAssignments: string;
      columns: {
        deliveryPointOwner: string;
        balanceGroup: string;
        mds: string;
        validity: string;
      };
    };
    gdsDevicesList: {
      title: string;
      id: string;
      name: string;
      columns: {
        DeviceId: string;
        DeviceType: string;
        actions: string;
      };
    };
    mdsTab: {
      title: string;
      columns: {
        eic: string;
        id: string;
        name: string;
      };
    };
  };
  deliveryPointList: {
    title: string;
    searchInput: string;
    createEntityButton: string;
    columns: {
      id: string;
      eic: string;
      name: string;
      role: string;
      address: string;
      actions: string;
    };
    filter: i18nEaiDeliveryPointListFilter;
  };
  input: {
    DeliveryPointName: string;
    DeliveryPointEic: string;
    DeliveryPointRole: string;
    name: string;
  };
  validation: {
    'DeliveryPointName-required': string;
    'DeliveryPointName-max': string;
    'DeliveryPointGDSName-required': string;
    'DeliveryPointGDSName-max': string;
    'DeliveryPointEic-required': string;
    'DeliveryPointEic-length': string;
    'DeliveryPointEic-available': string;
    'DeliveryPointRole-required': string;
    'DeliveryPointRole-max': string;
  };
  createGDSDialog: {
    input: {
      balancingGroupId: string;
      marketParticipantId: string;
    };
  };
  changeDPAssignmentDialog: {
    input: {
      balanceGroup: string;
      marketParticipant: string;
      deliveryPoint: string;
    };
    validation: {
      'BalanceGroup-required': string;
      'MarketParticipant-required': string;
      'DeliveryPoint-required': string;
    };
  };
};

type i18nEaiDeliveryPointListFilter = {
  role: string;
};

type i18nHttpErrorStatusMessages = {
  error400BadRequest: string;
  error401Unauthorized: string;
  error403Forbidden: string;
  error404NotFound: string;
  error500InternalError: string;
};

type i18nCommonQualityFlags = {
  qualityFlag: string;
  qualityFlagCodes: i18nCommonQualityFlagCodes;
};

type i18nCommonQualityFlagCodes = {
  OK: string;
  A: string;
  SB: string;
  T: string;
  M: string;
  OV: string;
  NT: string;
  OB: string;
  IV: string;
};

export type i18nHomePage = {
  title: string;
  emsPartyName: string;
  accountType: string;
  roles: string;
  dateTime: string;
  AIEMSDescription: string;
  appDescription: string;
};

export type i18nReporting = {
  economicBenefit: {
    aggregation: {
      [AggregationFn.MEAN]: string;
      [AggregationFn.CUMULATIVE_SUM]: string;
    };
    aggregationLabel: string;
    aggregationFrom: string;
    aggregationTo: string;
    evaluationDate: string;
    evaluation: {
      [BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_DAY]: string;
      [BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_MONTH]: string;
    };
    exportToCsv: {
      dialogTitle: string;
      evaluationSelectLabel: string;
      openExportModalButton: string;
      reportType: {
        [BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_PERIOD]: string;
        [BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_HOUR]: string;
        [BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_DAY]: string;
      };
      columns: { [key in EconomicBenefitReportFields]: string };
    };
    tableColumns: {
      realBenefit: string;
      withoutAORTA: string;
      withAORTA: string;
      difference: string;
    };
    tableRows: {
      deviation: string;
      unitCostOfDeviation: string;
      deviationSize: string;
      costOfExceedingReservedCapacity: string;
    };
    tableTooltip: {
      energy: string;
      exceedingCount: string;
    };
    charts: {
      benefitChartsTitle: string;
      cumulativeBenefitsChartTitle: string;
      cost: {
        [BenefitCostType.UNIT]: string;
        [BenefitCostType.SUMMARY]: string;
      };
      labels: {
        period: string;
        day: string;
      };
      series: {
        [EconomicBenefitReportFields.TOTAL_IMBALANCE_PRICE_EUR]: string;
        [EconomicBenefitReportFields.TOTAL_IMBALANCE_MODEL_PRICE_EUR]: string;
      };
    };
  };
  tableColumns: {
    date: string;
    period: string;
    period_start: string;
    period_end: string;
    system_imbalance_mwh: string;
    system_imbalance_eur_mwh: string;
    predicted_value_d1: string;
    predicted_value_hx: string;
    prediction_success_d1: string;
    prediction_success_hx: string;
    ems_party_id: string;
    ems_party_name: string;
    balancing_group_id: string;
    balancing_group_name: string;
    delivery_point_id: string;
    delivery_point_name: string;
    device_id: string;
    device_name: string;
    load_nomination: string;
    load_plan: string;
    load_real: string;
    load_imbalance: string;
    generation_nomination: string;
    generation_plan: string;
    generation_real: string;
  };
  dateFrom: string;
  dateTo: string;
};

type i18nSystemActivityEvents = {
  tableColumns: {
    createdDateTime: string;
    code: string;
    logLevel: string;
    event: string;
    tags: string;
    creator: string;
    emsPartyId: string;
    emsPartyName: string;
    balancingGroupId: string;
    balancingGroupName: string;
    deviceId: string;
    deviceName: string;
  };
  filter: {
    dateTimeFilterValue: string;
    beforeFilterLabel: string;
    onOrAfterFilterLabel: string;
  };
  dateFrom: string;
  dateTo: string;
  dateRangeError: string;
};

export type i18nSystemCodebooks = {
  ElectricalPowerUnit: {
    W: i18nInput;
    kW: i18nInput;
    MW: i18nInput;
    GW: i18nInput;
  };
  PredictionErrorCode: {
    WEATHER_DATA_NOT_AVAILABLE: i18nInput;
    LOW_RELIABILITY_INCOMPLETE_INPUT_DATA_INTERPOLATING: i18nInput;
    LOW_RELIABILITY_INCOMPLETE_INPUT_DATA_FILL_WITH_CONSTANT: i18nInput;
    LOW_RELIABILITY_OPTIMIZATION_UNFINISHED: i18nInput;
    LOW_RELIABILITY_OTHER: i18nInput;
    NOK: i18nInput;
    OK: i18nInput;
    NOK_OPTIMIZATION_INFEASIBLE: i18nInput;
    MISSING_CRITICAL_INPUT_DATA_EXCLUDING_DEVICE: i18nInput;
    MISSING_CRITICAL_INPUT_DATA_OPTIMIZATION_FAILED: i18nInput;
    UNSUPPORTED_TOPOLOGY_OF_BALANCE_GROUP: i18nInput;
  };
  EntsoeSystemImbalanceLoadingType: {
    intermediate: i18nInput;
    final: i18nInput;
  };
  EntsoeWeatherEnergyType: { wind: i18nInput; solar: i18nInput };
  OkteSystemImbalanceLoadingType: {
    preliminarydaily: i18nInput;
    regulardaily: i18nInput;
  };
  OperationEvent: {
    str_PWD: i18nInput;
    por_charg: i18nInput;
    por_bat: i18nInput;
    str_sig_U: i18nInput;
    kom_elm_prah: i18nInput;
    kom_elm_svorky: i18nInput;
    ovl_miestne: i18nInput;
    HRM_BLOK: i18nInput;
    por_ochr: i18nInput;
    GP: i18nInput;
    FU_por: i18nInput;
    por: i18nInput;
    kom_BESS: i18nInput;
  };
  ReservedCapacityGuardingCheckResultStatus: {
    [ReservedCapacityGuardingCheckResultStatus.ACTIONS_NOT_NEEDED]: {
      title: string;
    };
    [ReservedCapacityGuardingCheckResultStatus.ACTIONS_ISSUED]: {
      title: string;
    };
    [ReservedCapacityGuardingCheckResultStatus.ACTIONS_ISSUED_RESERVED_CAPACITY_EXCEEDED]: {
      title: string;
    };
    [ActivityLogEventCode.RESERVED_CAPACITY_EXCEEDED_NO_ACTIONS_POSSIBLE]: {
      title: string;
    };
  };
  ReservedCapacityGuardingActionType: {
    [ReservedCapacityGuardingActionTypeEnum.Increase]: {
      title: string;
    };
    [ReservedCapacityGuardingActionTypeEnum.Reduction]: {
      title: string;
    };
  };
};

export type i18nSystemActivityLogs = {
  [ActivityLogEventCode.OPERATION_EVENT_CANCELED]: string;
  [ActivityLogEventCode.OPERATION_EVENT_DELETED]: string;
  [ActivityLogEventCode.CONTRACT_FULFILLMENT_OPERATION_EVENT_SCHEDULED]: string;
  [ActivityLogEventCode.CONTRACT_FULFILLMENT_OPERATION_EVENT_EDITED]: string;
  [ActivityLogEventCode.PRODUCTION_AVAILABILITY_OPERATION_EVENT_SCHEDULED]: string;
  [ActivityLogEventCode.PRODUCTION_UNAVAILABLE_OPERATION_EVENT_SCHEDULED]: string;
  [ActivityLogEventCode.PRODUCTION_AVAILABILITY_OPERATION_EVENT_EDITED]: string;
  [ActivityLogEventCode.PRODUCTION_UNAVAILABLE_OPERATION_EVENT_EDITED]: string;
  [ActivityLogEventCode.PRODUCTION_PLAN_REVISION_CREATED]: string;
  [ActivityLogEventCode.PRODUCTION_PLAN_REVISION_UPDATED]: string;
  [ActivityLogEventCode.PRODUCTION_PLAN_REVISION_CONFIRMED]: string;
  [ActivityLogEventCode.PRODUCTION_PLAN_REVISION_PROPAGATED]: string;
  [ActivityLogEventCode.DAILY_DIAGRAM_REVISION_ACCEPTED]: string;
  [ActivityLogEventCode.DAILY_DIAGRAM_REVISION_RETURNED]: string;
  [ActivityLogEventCode.PRODUCTION_PLAN_REVISION_PLANNED]: string;
  [ActivityLogEventCode.PRODUCTION_PLAN_REVISION_REJECTED]: string;
  [ActivityLogEventCode.DAILY_DIAGRAM_REVISION_CREATED]: string;
  [ActivityLogEventCode.DAILY_DIAGRAM_REVISION_UPDATED]: string;
  [ActivityLogEventCode.DAILY_DIAGRAM_REVISION_SENT]: string;
  [ActivityLogEventCode.DAILY_DIAGRAM_REVISION_PROPAGATION_FAILED]: string;
  [ActivityLogEventCode.DAY_AHEAD_SYSTEM_IMBALANCE_PREDICTION_GENERATED_SUCCESS]: string;
  [ActivityLogEventCode.DAY_AHEAD_SYSTEM_IMBALANCE_PREDICTION_GENERATED_WARNING]: string;
  [ActivityLogEventCode.DAY_AHEAD_SYSTEM_IMBALANCE_PREDICTION_GENERATED_ERROR]: string;
  [ActivityLogEventCode.INTRA_DAY_SYSTEM_IMBALANCE_PREDICTION_UPDATED_SUCCESS]: string;
  [ActivityLogEventCode.INTRA_DAY_SYSTEM_IMBALANCE_PREDICTION_UPDATED_WARNING]: string;
  [ActivityLogEventCode.INTRA_DAY_SYSTEM_IMBALANCE_PREDICTION_UPDATED_ERROR]: string;
  [ActivityLogEventCode.DAY_AHEAD_DAM_PRICE_PREDICTION_GENERATED_SUCCESS]: string;
  [ActivityLogEventCode.DAY_AHEAD_DAM_PRICE_PREDICTION_GENERATED_WARNING]: string;
  [ActivityLogEventCode.DAY_AHEAD_DAM_PRICE_PREDICTION_GENERATED_ERROR]: string;
  [ActivityLogEventCode.DAY_AHEAD_IDM_PRICE_15M_PREDICTION_GENERATED_SUCCESS]: string;
  [ActivityLogEventCode.DAY_AHEAD_IDM_PRICE_15M_PREDICTION_GENERATED_WARNING]: string;
  [ActivityLogEventCode.DAY_AHEAD_IDM_PRICE_15M_PREDICTION_GENERATED_ERROR]: string;
  [ActivityLogEventCode.DAY_AHEAD_IDM_PRICE_60M_PREDICTION_GENERATED_SUCCESS]: string;
  [ActivityLogEventCode.DAY_AHEAD_IDM_PRICE_60M_PREDICTION_GENERATED_WARNING]: string;
  [ActivityLogEventCode.DAY_AHEAD_IDM_PRICE_60M_PREDICTION_GENERATED_ERROR]: string;
  [ActivityLogEventCode.INTRA_DAY_IDM_PRICE_15M_PREDICTION_UPDATED_SUCCESS]: string;
  [ActivityLogEventCode.INTRA_DAY_IDM_PRICE_15M_PREDICTION_UPDATED_WARNING]: string;
  [ActivityLogEventCode.INTRA_DAY_IDM_PRICE_15M_PREDICTION_UPDATED_ERROR]: string;
  [ActivityLogEventCode.INTRA_DAY_IDM_PRICE_60M_PREDICTION_UPDATED_SUCCESS]: string;
  [ActivityLogEventCode.INTRA_DAY_IDM_PRICE_60M_PREDICTION_UPDATED_WARNING]: string;
  [ActivityLogEventCode.INTRA_DAY_IDM_PRICE_60M_PREDICTION_UPDATED_ERROR]: string;
  [ActivityLogEventCode.DAY_AHEAD_SYSTEM_IMBALANCE_PRICE_PREDICTION_GENERATED_SUCCESS]: string;
  [ActivityLogEventCode.DAY_AHEAD_SYSTEM_IMBALANCE_PRICE_PREDICTION_GENERATED_WARNING]: string;
  [ActivityLogEventCode.DAY_AHEAD_SYSTEM_IMBALANCE_PRICE_PREDICTION_GENERATED_ERROR]: string;
  [ActivityLogEventCode.INTRA_DAY_SYSTEM_IMBALANCE_PRICE_PREDICTION_UPDATED_SUCCESS]: string;
  [ActivityLogEventCode.INTRA_DAY_SYSTEM_IMBALANCE_PRICE_PREDICTION_UPDATED_WARNING]: string;
  [ActivityLogEventCode.INTRA_DAY_SYSTEM_IMBALANCE_PRICE_PREDICTION_UPDATED_ERROR]: string;
  [ActivityLogEventCode.DAY_AHEAD_DEVICE_PRODUCTION_PLAN_PREDICTION_GENERATED_SUCCESS]: string;
  [ActivityLogEventCode.DAY_AHEAD_DEVICE_PRODUCTION_PLAN_PREDICTION_GENERATED_WARNING]: string;
  [ActivityLogEventCode.DAY_AHEAD_DEVICE_PRODUCTION_PLAN_PREDICTION_GENERATED_ERROR]: string;
  [ActivityLogEventCode.INTRA_DAY_DEVICE_PRODUCTION_PLAN_PREDICTION_UPDATED_SUCCESS]: string;
  [ActivityLogEventCode.INTRA_DAY_DEVICE_PRODUCTION_PLAN_PREDICTION_UPDATED_WARNING]: string;
  [ActivityLogEventCode.INTRA_DAY_DEVICE_PRODUCTION_PLAN_PREDICTION_UPDATED_ERROR]: string;
  [ActivityLogEventCode.DAY_AHEAD_DELIVERY_POINT_PRODUCTION_PLAN_PREDICTION_GENERATED_SUCCESS]: string;
  [ActivityLogEventCode.DAY_AHEAD_DELIVERY_POINT_PRODUCTION_PLAN_PREDICTION_GENERATED_WARNING]: string;
  [ActivityLogEventCode.DAY_AHEAD_DELIVERY_POINT_PRODUCTION_PLAN_PREDICTION_GENERATED_ERROR]: string;
  [ActivityLogEventCode.INTRA_DAY_DELIVERY_POINT_PRODUCTION_PLAN_PREDICTION_GENERATED_SUCCESS]: string;
  [ActivityLogEventCode.INTRA_DAY_DELIVERY_POINT_PRODUCTION_PLAN_PREDICTION_GENERATED_WARNING]: string;
  [ActivityLogEventCode.INTRA_DAY_DELIVERY_POINT_PRODUCTION_PLAN_PREDICTION_GENERATED_ERROR]: string;
  [ActivityLogEventCode.DAY_AHEAD_BALANCING_GROUP_IMBALANCE_PLAN_GENERATED_SUCCESS]: string;
  [ActivityLogEventCode.DAY_AHEAD_BALANCING_GROUP_IMBALANCE_PLAN_GENERATED_WARNING]: string;
  [ActivityLogEventCode.DAY_AHEAD_BALANCING_GROUP_IMBALANCE_PLAN_GENERATED_ERROR]: string;
  [ActivityLogEventCode.INTRA_DAY_BALANCING_GROUP_IMBALANCE_PLAN_UPDATED_SUCCESS]: string;
  [ActivityLogEventCode.INTRA_DAY_BALANCING_GROUP_IMBALANCE_PLAN_UPDATED_WARNING]: string;
  [ActivityLogEventCode.INTRA_DAY_BALANCING_GROUP_IMBALANCE_PLAN_UPDATED_ERROR]: string;
  [ActivityLogEventCode.DAY_AHEAD_BALANCING_GROUP_DIAGRAM_PREDICTION_GENERATED_SUCCESS]: string;
  [ActivityLogEventCode.DAY_AHEAD_BALANCING_GROUP_DIAGRAM_PREDICTION_GENERATED_WARNING]: string;
  [ActivityLogEventCode.DAY_AHEAD_BALANCING_GROUP_DIAGRAM_PREDICTION_GENERATED_ERROR]: string;
  [ActivityLogEventCode.INTRA_DAY_BALANCING_GROUP_DIAGRAM_PREDICTION_UPDATED_SUCCESS]: string;
  [ActivityLogEventCode.INTRA_DAY_BALANCING_GROUP_DIAGRAM_PREDICTION_UPDATED_WARNING]: string;
  [ActivityLogEventCode.INTRA_DAY_BALANCING_GROUP_DIAGRAM_PREDICTION_UPDATED_ERROR]: string;
  [ActivityLogEventCode.ENTSOE_IMBALANCE_DATA_LOADED]: string;
  [ActivityLogEventCode.ENTSOE_DAY_AHEAD_GENERATION_DATA_LOADED]: string;
  [ActivityLogEventCode.ENTSOE_DAY_AHEAD_LOAD_DATA_LOADED]: string;
  [ActivityLogEventCode.ENTSOE_ACTUAL_LOAD_DATA_LOADED]: string;
  [ActivityLogEventCode.ENTSOE_DAY_AHEAD_PRICES_DATA_LOADED]: string;
  [ActivityLogEventCode.ENTSOE_DAY_AHEAD_WIND_SOLAR_GENERATION_DATA_LOADED]: string;
  [ActivityLogEventCode.OKTE_IMBALANCE_DATA_LOADED]: string;
  [ActivityLogEventCode.OKTE_SHORTTERM_MARKET_DATA_LOADED]: string;
  [ActivityLogEventCode.OKTE_INTRADAY_MARKET60MIN_DATA_LOADED]: string;
  [ActivityLogEventCode.OKTE_INTRADAY_MARKET15MIN_DATA_LOADED]: string;
  [ActivityLogEventCode.SOLCAST_WEATHER_FORECAST_LOADED]: string;
  [ActivityLogEventCode.SOLCAST_WEATHER_HISTORIC_LOADED]: string;
  [ActivityLogEventCode.SOLCAST_DEVICE_WEATHER_FORECAST_LOADED]: string;
  [ActivityLogEventCode.SOLCAST_DEVICE_WEATHER_HISTORIC_LOADED]: string;
  [ActivityLogEventCode.DEVICE_OPERATION_EVENT_OCCURRED]: string;
  [ActivityLogEventCode.DEVICE_OPERATION_EVENT_RESOLVED]: string;
  [ActivityLogEventCode.DELIVERY_POINT_OPERATION_EVENT_OCCURRED]: string;
  [ActivityLogEventCode.DELIVERY_POINT_OPERATION_EVENT_RESOLVED]: string;
  [ActivityLogEventCode.PREDICTION_TASK_FAILED]: string;
  [ActivityLogEventCode.RESERVED_CAPACITY_GUARD_MODE_CHANGED]: string;
  [ActivityLogEventCode.RESERVED_CAPACITY_GUARDING_CHECK_REQUESTED]: string;
  [ActivityLogEventCode.RESERVED_CAPACITY_GUARDING_CHECK_SUCCEEDED]: string;
  [ActivityLogEventCode.RESERVED_CAPACITY_GUARDING_CHECK_FAILED]: string;
  [ActivityLogEventCode.RESERVED_CAPACITY_GUARDING_ACTION_ACCEPTED]: string;
  [ActivityLogEventCode.RESERVED_CAPACITY_GUARDING_ACTION_REJECTED]: string;
  [ActivityLogEventCode.RESERVED_CAPACITY_UTILIZATION_WARNING]: string;
  [ActivityLogEventCode.RESERVED_CAPACITY_EXCEEDED]: string;
  [ActivityLogEventCode.RESERVED_CAPACITY_EXCEEDED_NO_ACTIONS_POSSIBLE]: string;
  [ActivityLogEventCode.RESERVED_CAPACITY_GUARDING_ACTIONS_ISSUED]: string;
  [ActivityLogEventCode.RESERVED_CAPACITY_GUARDING_ACTIONS_ACCEPTED]: string;
};

export type i18nSystemAlarms = {
  resolveAlarmDialog: {
    title: string;
    description: string;
    inputLabel: string;
    resolveButton: string;
  };
  cancelAlarmDialog: {
    title: string;
    description: string;
    inputLabel: string;
    cancelButton: string;
  };
  filter: {
    triggeredFrom: string;
    triggeredTo: string;
    buttonApply: string;
    dateRangeError: string;
  };
  table: {
    columns: {
      [AlarmsColumn.TRIGGERED_AT]: string;
      [AlarmsColumn.CODE]: string;
      [AlarmsColumn.SEVERITY]: string;
      [AlarmsColumn.STATUS]: string;
      [AlarmsColumn.PARAMETERS]: string;
      [AlarmsColumn.TAGS]: string;
      [AlarmsColumn.MARKET_PARTICIPANT]: string;
      [AlarmsColumn.BALANCING_GROUP]: string;
      [AlarmsColumn.DELIVERY_POINT]: string;
      [AlarmsColumn.DEVICE]: string;
      [AlarmsColumn.ACTIONS]: string;
    };
  };
};
